// Resumo por cliente: uma fase, um próximo passo e uma situação por linha.
import { taskScheduleLabel } from "@/lib/productionTable";

export const PHASES = [
  { key: "planejar", label: "Planejar", etapas: ["planejamento"] },
  { key: "roteiro", label: "Roteiro", etapas: ["pre_producao"] },
  { key: "captar", label: "Captar", etapas: ["captacao"] },
  { key: "editar", label: "Editar", etapas: ["pos_producao"] },
  { key: "aprovar", label: "Aprovar", etapas: ["aprovacao"] },
  { key: "publicar", label: "Publicar", etapas: ["programacao", "publicacao"] },
];

export const SITUATIONS = {
  atrasado: { label: "Atrasado", order: 0 },
  hoje: { label: "Hoje", order: 1 },
  amanha: { label: "Amanhã", order: 2 },
  cliente: { label: "Com o cliente", order: 3 },
  em_dia: { label: "Em dia", order: 4 },
  sem_ciclo: { label: "Sem ciclo", order: 5 },
  concluido: { label: "Concluído", order: 6 },
};

const isoDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const isDated = (task) => /^\d{4}-\d{2}-\d{2}$/.test(task.prazo || "");
const isOpen = (task) => task.status !== "concluido" && task.status !== "cancelado";
const finished = (cycle) => cycle.etapa === "finalizado" || cycle.status === "concluido";

// Índice da fase (0-5); 6 quando o ciclo terminou.
export function phaseIndex(cycle) {
  if (finished(cycle)) return PHASES.length;
  const index = PHASES.findIndex((phase) => phase.etapas.includes(cycle.etapa));
  return index < 0 ? 0 : index;
}

export function daysBetween(from, to) {
  return Math.round((new Date(`${to}T12:00:00`) - new Date(`${from}T12:00:00`)) / 86400000);
}

export function summarizeRow(row, now = new Date()) {
  const today = isoDate(now);
  const tomorrow = isoDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  if (!row.cycles.length) return { ...row, phase: -1, next: null, situation: "sem_ciclo" };
  // Com mais de um ciclo no mês, vale o mais atrasado no fluxo.
  const phase = Math.min(...row.cycles.map(phaseIndex));
  const open = row.tasks.filter(isOpen)
    .sort((a, b) => (isDated(a) ? a.prazo : "9999").localeCompare(isDated(b) ? b.prazo : "9999") || (a.titulo || "").localeCompare(b.titulo || ""));
  const next = open[0] || null;
  const late = open.filter((task) => isDated(task) && task.planejamento_condicao !== "proposta" && task.prazo < today);
  // "Com o cliente" vem do status do ciclo, marcado quando o estúdio envia.
  // Marcações antigas fora da fase de aprovação não contam.
  const waiting = row.cycles.find((cycle) => cycle.status === "aguardando_cliente" && PHASES[phaseIndex(cycle)]?.key === "aprovar");
  let situation = "em_dia";
  if (phase >= PHASES.length && !open.length) situation = "concluido";
  else if (waiting) situation = "cliente";
  else if (late.length) situation = "atrasado";
  else if (next && isDated(next) && next.prazo === today) situation = "hoje";
  else if (next && isDated(next) && next.prazo === tomorrow) situation = "amanha";
  const lateDays = late.length ? Math.max(...late.map((task) => daysBetween(task.prazo, today))) : 0;
  const waitingDays = waiting && /^\d{4}-\d{2}-\d{2}$/.test(waiting.aguardando_desde || "") ? Math.max(0, daysBetween(waiting.aguardando_desde, today)) : 0;
  return { ...row, phase, next, situation, lateDays, lateCount: late.length, open: open.length, waiting: waiting || null, waitingDays };
}

export function situationLabel(summary) {
  if (summary.situation === "atrasado") return summary.lateDays === 1 ? "1 dia de atraso" : `${summary.lateDays} dias de atraso`;
  if (summary.situation === "cliente" && summary.waitingDays) return `Com o cliente · ${summary.waitingDays}d`;
  return SITUATIONS[summary.situation].label;
}

export function nextStepLabel(task) {
  if (!task) return "";
  return [task.responsavel, taskScheduleLabel(task)].filter(Boolean).join(" · ");
}

export function summarizeRows(rows, now = new Date()) {
  return rows.map((row) => summarizeRow(row, now))
    .sort((a, b) => SITUATIONS[a.situation].order - SITUATIONS[b.situation].order || b.lateDays - a.lateDays || a.client.empresa.localeCompare(b.client.empresa, "pt-BR"));
}

export function countBySituation(summaries) {
  return summaries.reduce((acc, item) => ({ ...acc, [item.situation]: (acc[item.situation] || 0) + 1 }), {});
}
